import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  KULCINA_STROKE_MS,
  kulcinaOpsFromHex,
  type KulcinaOp,
  type KulcinaState,
} from '@movienight/shared';
import { commitKulcinaOps, getKulcina } from '../lib/api';
import { useSocket } from '../lib/SocketProvider';
import { applyOps, cacheBits, loadCachedBits, setFromHex, toHex } from './display';

/**
 * Everything that makes Kulčina's display shared, in one place, so the editor
 * can stay a thing about geometry and pointers.
 *
 * Four rules explain all of it:
 *
 *  1. **Cells, not grids — for everything.** Drawing is sent as `[cell, on]`
 *     operations, so two people drawing at once merge and only a cell they
 *     both painted has a winner. Reset and Clear are the same thing with an op
 *     for every cell. They used to be a whole-grid write and it caused both
 *     halves of a reported bug: the other screen only cleared a round trip
 *     later, and a commit landing in between put the old drawing back.
 *
 *  2. **An uncommitted stroke outranks an authoritative grid, whosever it is.**
 *     `pending` holds the cells you have drawn and not committed; `live` holds
 *     the same for everyone else, keyed by who is drawing. Both are re-applied
 *     on top of any grid the server sends, which is what stops a commit
 *     landing mid-stroke from blinking a line out and back. An author's entry
 *     in `live` is dropped the moment their own commit arrives, because from
 *     then on the grid carries it.
 *
 *  3. **Newest wins, not last-to-arrive.** Every server state carries a
 *     version the database owns, and anything not newer than what is already
 *     applied is dropped. A commit's HTTP response can arrive after the socket
 *     frame for a *later* commit; without this the older grid lands second and
 *     rolls the display back on that one screen, permanently.
 *
 *  4. **The socket is the preview, REST is the save.** Frames sent while the
 *     pointer is down are never written down by anyone; the PATCH when it
 *     comes up is. Lose the socket and you lose the live preview, not the
 *     drawing.
 *
 * What is on screen is *derived*, never accumulated:
 *
 *     bits = base (last authoritative grid) + live (theirs) + pending (mine)
 *
 * and `recompose` rebuilds it whenever any of the three changes. Mutating
 * `bits` in place instead is what made two browsers disagree permanently: a
 * commit's response removed cells from `pending`, but nothing recomputed the
 * display, so those cells stayed painted on that one screen with nothing left
 * that would ever take them off. Adding a cell is the one safe mutation — it
 * only ever goes on top — so the pointer path keeps its fast path.
 *
 * Repaints do not go through React. A drag fires ~60 pointer moves a second
 * and re-rendering the whole night sky for each one is wasted work, so both
 * canvases register a paint callback here and get called directly.
 */

export interface KulcinaSync {
  /** Mutated in place, never replaced — both canvases hold this same array. */
  bits: Uint8Array;
  artist: { nickname: string; at: string } | null;
  /** Register a repaint; returns an unsubscribe. */
  watch: (repaint: () => void) => () => void;
  paintCell: (index: number, on: 0 | 1) => void;
  /** Pointer up: flush the preview, then commit the stroke. */
  endStroke: () => void;
  /** Reset and Clear — a stroke that happens to touch every cell. */
  replaceAll: (hex: string) => void;
}

export function useKulcina(): KulcinaSync {
  const { socket, onResync } = useSocket();

  // Seeded from the cache so the tower shows the right drawing on the first
  // frame rather than flashing the built-in default while GET is in flight.
  const bits = useMemo(() => loadCachedBits(), []);
  const [artist, setArtist] = useState<{ nickname: string; at: string } | null>(null);

  const watchers = useRef(new Set<() => void>());
  const repaint = useCallback(() => {
    for (const fn of watchers.current) fn();
  }, []);
  const watch = useCallback((fn: () => void) => {
    watchers.current.add(fn);
    return () => {
      watchers.current.delete(fn);
    };
  }, []);

  /** Drawn locally, not yet acknowledged by the server. Rule 2. */
  const pending = useRef(new Map<number, 0 | 1>());
  /**
   * The same, for everyone else's strokes in progress, keyed by who is drawing.
   *
   * Without this the protection is one-sided: your own line survives someone
   * else's commit, but theirs does not survive yours — their stroke vanishes
   * from your screen the moment you let go of your own, and reappears when
   * they let go of theirs. It is not lost, but it flickers, and a shared
   * drawing that flickers reads as a broken one.
   */
  const live = useRef(new Map<string, Map<number, 0 | 1>>());
  /**
   * Drawn locally, not yet sent over the socket. Keyed by cell rather than a
   * list, so it holds the latest value per cell and can never exceed the grid
   * — two Clears inside one flush window would otherwise build a 960-op frame,
   * which is over the hub's cap, and the hub would drop the whole thing.
   */
  const outbox = useRef(new Map<number, 0 | 1>());
  const flushTimer = useRef<number | null>(null);
  /** Highest version applied. Rule 3. */
  const seen = useRef(-1);
  /** The last grid the server vouched for. Everything else layers on top. */
  const base = useRef('');
  if (base.current === '') base.current = toHex(bits);

  /**
   * Rebuild the display from its three sources. Cheap — 480 cells and a couple
   * of small maps — and being able to call it after *any* change is what keeps
   * the screen honest when a state arrives out of order.
   */
  const recompose = useCallback(() => {
    setFromHex(bits, base.current);
    for (const cells of live.current.values()) applyOps(bits, [...cells]);
    // Mine last, so a cell two people are drawing at once looks like mine until
    // one of us commits — the same last-writer-wins the server applies.
    applyOps(bits, [...pending.current]);
    repaint();
  }, [bits, repaint]);

  /** Take a server state as the new base, if it is actually newer. */
  const adopt = useCallback(
    (state: KulcinaState, opts: { committedBy?: string; hard?: boolean } = {}) => {
      // Rule 3. A full refetch is authoritative by definition and skips the
      // check, which is also the escape hatch if the counter ever goes
      // backwards — a restored database would otherwise deadlock every client.
      if (!opts.hard && state.version <= seen.current) return;
      seen.current = state.version;
      base.current = state.bits;

      // Cached from the server's grid alone, before anything uncommitted goes
      // on top, so a reload never resurrects a stroke the server rejected.
      const cache = new Uint8Array(bits.length);
      if (setFromHex(cache, state.bits)) cacheBits(cache);

      // A hard resync is the one moment we know nothing about what anyone is
      // mid-way through, so strokes that never reached a commit — a closed tab,
      // a dropped socket — stop here rather than haunting the grid forever.
      if (opts.hard) live.current.clear();
      // Their commit is in the grid now; their entry has nothing left to say.
      else if (opts.committedBy) live.current.delete(opts.committedBy);

      setArtist(
        state.drawnBy && state.drawnAt
          ? { nickname: state.drawnBy.nickname, at: state.drawnAt }
          : null,
      );
      recompose();
    },
    [bits, recompose],
  );

  // Load on mount, and again whenever the socket comes back — a refetch is
  // 120 characters and it deletes the whole question of what was missed.
  useEffect(() => {
    let alive = true;
    const load = () => {
      void getKulcina()
        .then((state) => {
          if (alive) adopt(state, { hard: true });
        })
        .catch(() => {
          // A 401 here means the session died, and App already sends them back
          // to the door for that. Anything else leaves the cached drawing up,
          // which is a better answer than a blank tower.
        });
    };
    load();
    const off = onResync(load);
    return () => {
      alive = false;
      off();
    };
  }, [adopt, onResync]);

  useEffect(
    () =>
      socket.on((event) => {
        if (event.name === 'kulcina_ops') {
          const { byUserId, ops } = event.payload;

          // Remember all of them as theirs, including ones my own uncommitted
          // stroke currently outranks: when my commit lands and clears
          // `pending`, the next grid re-applies theirs and their line comes
          // back rather than staying invisible until they let go.
          let cells = live.current.get(byUserId);
          if (!cells) {
            cells = new Map();
            live.current.set(byUserId, cells);
          }
          for (const [index, on] of ops) cells.set(index, on);

          // Rule 2 falls out of the ordering in `recompose`: their cells go on
          // before mine, so anything I have uncommitted still wins on screen.
          recompose();
          return;
        }
        if (event.name === 'kulcina_drawn') {
          adopt(
            {
              bits: event.payload.bits,
              drawnBy: event.payload.drawnBy,
              drawnAt: event.payload.drawnAt,
              version: event.payload.version,
            },
            { committedBy: event.payload.byUserId },
          );
        }
      }),
    [socket, recompose, adopt],
  );

  const flush = useCallback(() => {
    if (flushTimer.current !== null) {
      window.clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
    if (outbox.current.size === 0) return;
    socket.draw([...outbox.current] as KulcinaOp[]);
    outbox.current.clear();
  }, [socket]);

  const paintCell = useCallback(
    (index: number, on: 0 | 1) => {
      // The one safe mutation: a pending cell always wins, so setting it
      // directly is what `recompose` would produce anyway, without rebuilding
      // the grid 60 times a second while the pointer is down.
      bits[index] = on;
      pending.current.set(index, on);
      outbox.current.set(index, on);
      if (flushTimer.current === null) {
        flushTimer.current = window.setTimeout(flush, KULCINA_STROKE_MS);
      }
      repaint();
    },
    [bits, flush, repaint],
  );

  const endStroke = useCallback(() => {
    flush();
    if (pending.current.size === 0) return;
    const ops = [...pending.current] as KulcinaOp[];

    void commitKulcinaOps(ops)
      .then((state) => {
        // Clear only what this commit carried, and only where the cell has not
        // been redrawn since — a stroke started while the request was in
        // flight must not be dropped along with the one that succeeded.
        for (const [index, on] of ops) {
          if (pending.current.get(index) === on) pending.current.delete(index);
        }

        // `adopt` may decide this state is stale — a socket frame for a later
        // commit can easily beat this response — but `pending` has shrunk
        // either way, and the screen has to be rebuilt to match. Skipping this
        // is exactly how one browser ended up holding cells that the grid no
        // longer had and nothing would ever clear.
        adopt(state);
        recompose();
      })
      .catch(() => {
        // Left in `pending`, so the next stroke's commit carries them again. A
        // save that fails is a save that retries, not a drawing that is lost.
      });
  }, [flush, adopt, recompose]);

  const replaceAll = useCallback(
    (hex: string) => {
      // Rule 1: Reset and Clear are a stroke that touches every cell. Going
      // through the same path is what makes them relay over the socket at
      // drawing speed and survive a commit landing while they are in flight.
      //
      // Every cell rather than a diff against what is on screen: a client whose
      // grid has drifted would compute a diff that leaves cells lit, and the
      // whole point of the button is that afterwards there is nothing there.
      const ops = kulcinaOpsFromHex(hex);
      if (!ops) return;
      for (const [index, on] of ops) {
        pending.current.set(index, on);
        outbox.current.set(index, on);
      }
      recompose();
      endStroke();
    },
    [recompose, endStroke],
  );

  useEffect(
    () => () => {
      if (flushTimer.current !== null) window.clearTimeout(flushTimer.current);
    },
    [],
  );

  // Memoised: consumers hold this object across renders, and a fresh one every
  // render would retrigger their effects for nothing.
  return useMemo(
    () => ({ bits, artist, watch, paintCell, endStroke, replaceAll }),
    [bits, artist, watch, paintCell, endStroke, replaceAll],
  );
}
